// TODO migrar a ActionMenu (@/kit): reemplazar el DropdownMenu local + <Gate> por items
// declarativos con permission/scope/scopeRef. Scope TENANT/tenantId (no PLATFORM) → pasar scopeRef.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { platformApi } from '../../../../api/endpoints/platform';
import { handleApiError } from '../../../../api/errorMapping';
import { useAuth } from '../../../../hooks/useAuth';
import { Gate } from '../../../../components/permissions/Gate';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../../../../components/ui/dropdown-menu';
import { Button } from '../../../../components/ui/button';
import { PlatformTenantRoleForm } from './PlatformTenantRoleForm';
import type { TenantRoleListItemLocal } from '../../../../api/types/TenantLocal';
import type { Permission } from '../../../../api/types/permissions';

// Permisos que habilitan alguna acción del menú. Si el user no tiene ninguno (ni scope sobre el
// tenant del path), no renderizamos el "⋮". Cada item igual se gatea con <Gate>. Son los permisos
// `tenant.role.*` del catálogo (el platform admin los tiene y alcanza el scope TENANT por rank).
const ACTION_PERMS: Permission[] = ['tenant.role.update', 'tenant.role.delete'];

interface Props {
    role: TenantRoleListItemLocal;
    /** tenantId del DRILL-DOWN (param de ruta), NO el del user logueado. */
    tenantId: string;
    /** Rol de sistema: solo "Ver detalle" (no editable/borrable). */
    isSystem: boolean;
}

/**
 * Menú "⋮" de acciones por fila de rol en el módulo Platform (drill-down del tenant). Editar y
 * eliminar van contra `platformApi.*TenantRole` sobre el `tenantId` del path, gateados por
 * `tenant.role.update`/`tenant.role.delete` con scope TENANT/tenantId (el platform admin lo cubre
 * por rank). El borrado pide confirmación. (Los roles de sistema NO renderizan este menú: lo decide
 * la tabla, que solo monta RowActions para roles custom.)
 */
export function PlatformTenantRoleRowActions({ role, tenantId, isSystem }: Props) {
    const qc = useQueryClient();
    const { hasPermission, hasScopeAccess } = useAuth();
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    // Editar/eliminar solo aplican a roles custom y si el user tiene el permiso + scope sobre el tenant.
    const canManage =
        !isSystem &&
        hasScopeAccess('TENANT', tenantId) &&
        ACTION_PERMS.some((p) => hasPermission(p));
    const permissions = role.permissions ?? [];

    const invalidate = (): Promise<void> =>
        qc.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId, 'roles'] });

    const deleteMut = useMutation({
        mutationFn: () => platformApi.deleteTenantRole(tenantId, role.roleId),
        onSuccess: async () => {
            toast.success('Rol eliminado');
            setConfirmDelete(false);
            await invalidate();
        },
        onError: handleApiError,
    });

    return (
        <>
            {/* modal={false}: evita el page-lock de Radix (pointer-events:none en body) cuando la
                fila se desmonta tras confirmar una acción. */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Acciones">
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    {/* Ver detalle: read-only, disponible para TODOS los roles (incluidos los de
                        sistema). Muestra los permisos del rol (ya vienen en la fila). */}
                    <DropdownMenuItem
                        onClick={() => {
                            setDetailOpen(true);
                        }}
                    >
                        Ver detalle
                    </DropdownMenuItem>
                    {canManage && (
                        <>
                            <DropdownMenuSeparator />
                            <Gate
                                permission="tenant.role.update"
                                scope="TENANT"
                                scopeRef={tenantId}
                            >
                                <DropdownMenuItem
                                    onClick={() => {
                                        setEditOpen(true);
                                    }}
                                >
                                    Editar
                                </DropdownMenuItem>
                            </Gate>
                            <Gate
                                permission="tenant.role.delete"
                                scope="TENANT"
                                scopeRef={tenantId}
                            >
                                <DropdownMenuItem
                                    onClick={() => {
                                        setConfirmDelete(true);
                                    }}
                                >
                                    Eliminar
                                </DropdownMenuItem>
                            </Gate>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Ver detalle: modal plano read-only con los permisos del rol. */}
            {detailOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setDetailOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold">{role.roleName}</h2>
                                <p className="text-xs text-muted-foreground">
                                    {isSystem ? 'Rol de sistema' : 'Rol custom'} ·{' '}
                                    {String(permissions.length)} permiso(s)
                                </p>
                            </div>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setDetailOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        {permissions.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                Este rol no tiene permisos asignados.
                            </p>
                        ) : (
                            <ul className="max-h-96 space-y-1 overflow-y-auto">
                                {[...permissions]
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((p) => (
                                        <li
                                            key={p}
                                            className="rounded bg-muted/50 px-2 py-1 font-mono text-xs"
                                        >
                                            {p}
                                        </li>
                                    ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title="Eliminar rol"
                description={`El rol "${role.roleName}" se eliminará. Los usuarios que lo tengan asignado perderán esos permisos. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />

            {/* Editar: modal plano (mismo patrón que la tabla de crear). */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setEditOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Editar rol</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setEditOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformTenantRoleForm
                            tenantId={tenantId}
                            role={role}
                            onDone={() => {
                                setEditOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
